import { IRegionShort } from 'api/region/types';
import axios, { AxiosResponse } from 'axios';
import { IS_NODE_ENV_DEVELOP, MEMCACHE_PORT } from 'ots-constants';
import { IResortPathInfo } from 'api/resort/types';
import { ICategoryNews } from 'api/home/types';

export const memcacheUrl = `http://localhost:${MEMCACHE_PORT}`;

// On Vercel there is no localhost memcache process. Resort/region slug
// lookups fall back to the bundled JSON index that webpack inlines via
// next.config.js's ContextReplacementPlugin -- the same source of truth
// the memcache server itself reads from at boot on ECS.
const IS_VERCEL = !!process.env.VERCEL;

let bundledResortsBySlug: Record<string, IResortPathInfo> | undefined;
let bundledRegionsBySlug: Record<string, IRegionShort> | undefined;

const getBundledResortsBySlug = (): Record<string, IResortPathInfo> => {
  if (!bundledResortsBySlug) {
    // eslint-disable-next-line global-require, import/no-dynamic-require
    const all = require(`../../public/index/resorts-${process.env.NEXT_PUBLIC_LANG}.json`) as Array<IResortPathInfo>;
    bundledResortsBySlug = {};
    all.forEach((r) => { bundledResortsBySlug![r.slug] = r; });
  }
  return bundledResortsBySlug;
};

const getBundledRegionsBySlug = (): Record<string, IRegionShort> => {
  if (!bundledRegionsBySlug) {
    // eslint-disable-next-line global-require, import/no-dynamic-require
    const file = require(`../../public/index/regions-${process.env.NEXT_PUBLIC_LANG}.json`) as { regions: Array<IRegionShort> };
    bundledRegionsBySlug = {};
    file.regions.forEach((r) => { bundledRegionsBySlug![r.slug] = r; });
  }
  return bundledRegionsBySlug;
};

const fakeAxiosResponse = <T>(data: T): AxiosResponse<T> => ({
  data,
  status: 200,
  statusText: 'OK',
  headers: {},
  config: {} as any,
});

// Mimics axios's "404 throws" behaviour so callers wrapped in
// assertShouldBeNot404() convert the miss into a NotFoundError.
const throwFakeAxios404 = (url: string): never => {
  const err: any = new Error(`Request failed with status code 404 (${url})`);
  err.isAxiosError = true;
  err.response = {
    status: 404, statusText: 'Not Found', data: null, headers: {}, config: {},
  };
  err.config = { url };
  throw err;
};

export const getUrlTranslations = async () => axios.get(`${memcacheUrl}/urls`);
// Remember slugs for fast access
export const cacheResortSlugs = async (paths: Array<IResortPathInfo>) => axios.post(`${memcacheUrl}/resorts`, paths);
export const getResortSlugsCache = async () => axios.get(`${memcacheUrl}/all-resorts`);
export const getResortSlug = async (slug: string): Promise<AxiosResponse<IResortPathInfo>> => {
  if (IS_VERCEL) {
    const hit = getBundledResortsBySlug()[slug];
    if (!hit) throwFakeAxios404(`/resort/${slug}`);
    return fakeAxiosResponse(hit!);
  }
  return axios.get(`${memcacheUrl}/resort/${slug}`);
};

export const cacheRegionSlugs = async (paths: Array<IRegionShort>) => axios.post(`${memcacheUrl}/regions`, paths);
export const getRegionSlugsCache = async () => axios.get(`${memcacheUrl}/all-regions`);
export const getRegionSlug = async (slug: string): Promise<AxiosResponse<IRegionShort>> => {
  if (IS_VERCEL) {
    const hit = getBundledRegionsBySlug()[slug];
    if (!hit) throwFakeAxios404(`/region/${slug}`);
    return fakeAxiosResponse(hit!);
  }
  return axios.get(`${memcacheUrl}/region/${slug}`);
};

export const cacheCategoryNewsSlugs = async (paths: Array<ICategoryNews>) => axios.post(`${memcacheUrl}/category`, paths);
export const getCategoryNewsSlugsCache = async () => axios.get(`${memcacheUrl}/all-categories`);
export const getCategoryNewsSlug = async (slug: string) => axios.get(`${memcacheUrl}/category/${slug}`);

export const corsProxy = async <T>(getRequest: string) => {
  if (IS_NODE_ENV_DEVELOP) {
    return axios.get<T>(`${memcacheUrl}/cors?url=${encodeURIComponent(getRequest)}`);
  }
  return axios.get<T>(getRequest);
};

export const cacheProxy = async <T>(getRequest: string) => axios.get<T>(`${memcacheUrl}/proxy?url=${encodeURIComponent(getRequest)}`);
